/**
 * BIKILA · Buscador predictivo para Showcase (basado en el buscador del tema Horizon).
 *
 * - Panel de búsqueda con resultados en vivo: sugerencias, productos, colecciones, páginas y artículos.
 * - Estado inicial: productos vistos recientemente + productos de una colección.
 * - Teclado: flechas / Tab para recorrer resultados, Enter para abrir, Esc para cerrar, Cmd/Ctrl + K.
 * - Barra compacta del header (.ps-bar): abre el panel y le pasa lo escrito.
 * - Miniaturas de color del mismo modelo (etiqueta "Modelo_…") en cada producto.
 */
(function () {
  'use strict';

  if (window.__bikilaPredictiveSearch) return;
  window.__bikilaPredictiveSearch = true;

  var RECENT_KEY = 'bikila:recently-viewed';
  var SWATCH_CACHE_PREFIX = 'bkms:ps1:';
  var SWATCH_CACHE_TTL = 10 * 60 * 1000;

  var reducedMotion = function () {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  function readRecent() {
    try {
      var list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      return Array.isArray(list) ? list.map(String).filter(Boolean) : [];
    } catch (e) {
      return [];
    }
  }

  function clearRecent() {
    try {
      localStorage.removeItem(RECENT_KEY);
    } catch (e) {
      /* sin almacenamiento */
    }
  }

  function parseHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  function fetchText(url, signal) {
    return fetch(url, { signal: signal, credentials: 'same-origin' }).then(function (response) {
      if (!response.ok) throw new Error('HTTP ' + response.status);
      return response.text();
    });
  }

  class PredictiveSearch {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-ps-form]');
      this.input = root.querySelector('[data-ps-input]');
      this.results = root.querySelector('[data-ps-results]');
      this.body = root.querySelector('[data-ps-body]');
      this.resetButton = root.querySelector('[data-ps-reset]');

      this.urls = {
        predictive: root.dataset.predictiveUrl || '/search/suggest',
        search: root.dataset.searchUrl || '/search',
        root: root.dataset.rootUrl || '/'
      };
      this.labels = {
        swatchView: root.dataset.labelSwatchView || '',
        swatchViewGeneric: root.dataset.labelSwatchViewGeneric || '',
        swatchMore: root.dataset.labelSwatchMore || ''
      };

      this.isOpen = false;
      this.lastTrigger = null;
      this.timer = 0;
      this.fetchId = 0;
      this.controller = null;
      this.emptyHTML = null;
      this.emptyPromise = null;
      this.recentCache = { key: '', html: '' };
      this.keyboardNav = false;
      this.swatchRequests = {};
      this.swatchItems = new WeakMap();

      this.bind();
    }

    /* ---------- Eventos ---------- */

    bind() {
      var self = this;

      this.input.addEventListener('input', function () {
        self.onInput();
      });

      this.resetButton.addEventListener('click', function () {
        self.reset(true);
      });

      this.root.addEventListener('keydown', function (event) {
        self.onKeydown(event);
      });

      this.root.addEventListener('click', function (event) {
        self.onClick(event);
      });

      // El botón "Ver todos los resultados" y Enter envían el formulario a la página de búsqueda.
      this.form.addEventListener('submit', function (event) {
        if (!self.input.value.trim()) event.preventDefault();
      });
    }

    onClick(event) {
      var target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      if (target.closest('[data-ps-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      var clear = target.closest('[data-ps-clear-recent]');
      if (clear) {
        event.preventDefault();
        event.stopPropagation();
        this.clearRecentProducts();
        return;
      }

      var scroller = target.closest('[data-ps-scroll]');
      if (scroller) {
        event.preventDefault();
        var list = scroller.closest('.ps-resource').querySelector('.ps-carousel');
        var step = list.clientWidth * 0.8 * Number(scroller.dataset.psScroll);
        list.scrollBy({ left: step, behavior: reducedMotion() ? 'auto' : 'smooth' });
        return;
      }

      var swatch = target.closest('.ps-swatches button');
      if (swatch) {
        event.preventDefault();
        event.stopPropagation();
        this.onSwatchClick(swatch);
        return;
      }

      // Clic en una zona sin controles: el foco vuelve al campo de búsqueda.
      if (!target.closest('a, button, input')) this.input.focus({ preventScroll: true });
    }

    onInput() {
      var term = this.input.value.trim();
      this.setIndex(-1);
      clearTimeout(this.timer);

      if (!term) {
        this.reset(false);
        return;
      }

      this.resetButton.hidden = false;
      var self = this;
      this.timer = setTimeout(function () {
        self.fetchResults(term);
      }, 200);
    }

    onKeydown(event) {
      var key = event.key;

      if (key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      var onInput = event.target === this.input;
      var items = this.items();

      if (key === 'Tab' && !onInput) {
        this.trapFocus(event);
        return;
      }

      // Las flechas horizontales mueven el cursor del texto.
      if (!onInput || !items.length || key === 'ArrowLeft' || key === 'ArrowRight') return;

      var current = this.currentIndex();
      var total = items.length;

      switch (key) {
        case 'ArrowDown':
          event.preventDefault();
          this.keyboardNav = true;
          this.setIndex(current < total - 1 ? current + 1 : 0);
          break;

        case 'ArrowUp':
          event.preventDefault();
          this.keyboardNav = true;
          this.setIndex(current > 0 ? current - 1 : total - 1);
          break;

        case 'Tab':
          event.preventDefault();
          this.keyboardNav = true;
          if (event.shiftKey) this.setIndex(current > 0 ? current - 1 : total - 1);
          else this.setIndex(current < total - 1 ? current + 1 : 0);
          break;

        case 'Enter': {
          var single = this.results.querySelector('[data-single-result-url]');
          if (single && single.dataset.singleResultUrl && this.input.value.trim()) {
            event.preventDefault();
            window.location.href = single.dataset.singleResultUrl;
            return;
          }
          if (current >= 0) {
            event.preventDefault();
            var link = items[current].querySelector('a[href]');
            if (link) link.click();
          }
          // Sin elemento seleccionado, Enter envía el formulario a la página de resultados.
          break;
        }
      }
    }

    trapFocus(event) {
      var focusable = Array.prototype.filter.call(
        this.root.querySelectorAll('a[href], button, input'),
        function (el) {
          return !el.disabled && !el.hidden && el.tabIndex !== -1 && el.offsetParent !== null;
        }
      );
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* ---------- Navegación con teclado ---------- */

    items() {
      return Array.prototype.slice.call(this.results.querySelectorAll('[data-ps-item]'));
    }

    currentIndex() {
      return this.items().findIndex(function (item) {
        return item.getAttribute('aria-selected') === 'true';
      });
    }

    setIndex(index) {
      var self = this;
      var activeItem = null;
      this.items().forEach(function (item, i) {
        item.classList.remove('keyboard-focus');
        if (i === index) {
          item.setAttribute('aria-selected', 'true');
          if (self.keyboardNav) item.classList.add('keyboard-focus');
          activeItem = item;
        } else {
          item.removeAttribute('aria-selected');
        }
      });
      if (index < 0) this.keyboardNav = false;
      if (activeItem) {
        activeItem.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'nearest' });
        this.input.focus({ preventScroll: true });
      }
    }

    /* ---------- Abrir y cerrar ---------- */

    open(value, trigger) {
      if (this.isOpen) {
        if (value) this.setValue(value);
        this.input.focus({ preventScroll: true });
        return;
      }

      this.isOpen = true;
      this.lastTrigger = trigger || document.activeElement;
      this.root.hidden = false;
      this.root.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('ps-lock');
      void this.root.offsetWidth;
      this.root.classList.add('is-open');

      document.querySelectorAll('[data-ps-open]').forEach(function (el) {
        el.setAttribute('aria-expanded', 'true');
      });

      this.showEmptyState();
      if (value) this.setValue(value);

      this.input.focus({ preventScroll: true });
    }

    close() {
      if (!this.isOpen) return;
      var self = this;
      this.isOpen = false;
      this.root.classList.remove('is-open');
      this.abort();
      clearTimeout(this.timer);

      document.querySelectorAll('[data-ps-open]').forEach(function (el) {
        el.setAttribute('aria-expanded', 'false');
      });

      var finish = function () {
        if (self.isOpen) return;
        self.root.hidden = true;
        self.root.setAttribute('aria-hidden', 'true');
        document.documentElement.classList.remove('ps-lock');
        self.reset(false);
      };
      if (reducedMotion()) finish();
      else setTimeout(finish, 220);

      var trigger = this.lastTrigger;
      if (trigger && typeof trigger.focus === 'function' && document.contains(trigger)) {
        trigger.focus({ preventScroll: true });
      }
    }

    toggle() {
      if (this.isOpen) this.close();
      else this.open();
    }

    setValue(value) {
      this.input.value = value;
      this.onInput();
    }

    /* ---------- Resultados ---------- */

    abort() {
      if (this.controller) this.controller.abort();
      this.controller = null;
    }

    fetchResults(term) {
      var self = this;
      var url = new URL(this.urls.predictive, window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('section_id', 'bikila-search-results');

      this.abort();
      var controller = (this.controller = new AbortController());
      var id = ++this.fetchId;
      this.root.classList.add('is-loading');

      fetchText(url.toString(), controller.signal)
        .then(function (html) {
          if (id !== self.fetchId) return;
          var results = parseHTML(html, '#predictive-search-results');
          if (!results) return;
          self.render(results.outerHTML, true);
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          console.warn('[BIKILA buscador]', error);
        })
        .then(function () {
          if (id === self.fetchId) self.root.classList.remove('is-loading');
        });
    }

    render(html, hasSearchResults) {
      this.results.innerHTML = html;
      this.results.scrollTop = 0;
      this.body.classList.toggle(
        'has-results',
        Boolean(hasSearchResults) &&
          Boolean(this.results.querySelector('[data-search-results]')) &&
          !this.results.querySelector('.ps-no-results')
      );
      this.input.setAttribute('aria-expanded', 'true');
      this.initSwatches(this.results);
    }

    /* ---------- Estado inicial (sin texto) ---------- */

    loadEmptyBase() {
      var self = this;
      if (this.emptyHTML !== null) return Promise.resolve(this.emptyHTML);
      if (this.emptyPromise) return this.emptyPromise;

      var url = new URL(this.urls.root, window.location.origin);
      url.searchParams.set('section_id', 'bikila-search-empty');

      this.emptyPromise = fetchText(url.toString())
        .then(function (html) {
          var node = parseHTML(html, '#predictive-search-results');
          self.emptyHTML = node ? node.outerHTML : '';
          return self.emptyHTML;
        })
        .catch(function (error) {
          console.warn('[BIKILA buscador]', error);
          self.emptyPromise = null;
          return '';
        });
      return this.emptyPromise;
    }

    loadRecentProducts() {
      var self = this;
      var ids = readRecent();
      if (!ids.length) return Promise.resolve(null);

      var key = ids.join(',');
      if (this.recentCache.key === key) return Promise.resolve(this.recentCache.html);

      var url = new URL(this.urls.search, window.location.origin);
      url.searchParams.set(
        'q',
        ids
          .map(function (id) {
            return 'id:' + id;
          })
          .join(' OR ')
      );
      url.searchParams.set('type', 'product');
      url.searchParams.set('section_id', 'bikila-search-results');

      return fetchText(url.toString())
        .then(function (html) {
          var wrapper = parseHTML(html, '[data-ps-recent]');
          var result = wrapper && wrapper.querySelector('[data-ps-item]') ? wrapper.outerHTML : null;
          self.recentCache = { key: key, html: result };
          return result;
        })
        .catch(function (error) {
          console.warn('[BIKILA buscador]', error);
          return null;
        });
    }

    showEmptyState() {
      var self = this;
      var id = ++this.fetchId;
      this.abort();
      this.root.classList.remove('is-loading');

      Promise.all([this.loadEmptyBase(), this.loadRecentProducts()]).then(function (parts) {
        if (id !== self.fetchId) return;
        var base = parts[0];
        var recent = parts[1];
        if (!base) return;

        var template = document.createElement('template');
        template.innerHTML = base;
        var products = template.content.querySelector('#predictive-search-products');
        if (recent && products) {
          var holder = document.createElement('div');
          holder.innerHTML = recent;
          products.prepend(holder.firstElementChild);
        } else if (recent && !products) {
          var inner = template.content.querySelector('.ps-results__inner');
          if (inner) inner.insertAdjacentHTML('afterbegin', recent);
        }
        self.render(template.innerHTML, false);
      });
    }

    reset(keepFocus) {
      clearTimeout(this.timer);
      this.input.value = '';
      this.resetButton.hidden = true;
      this.setIndex(-1);
      this.showEmptyState();
      if (keepFocus) this.input.focus({ preventScroll: true });
    }

    clearRecentProducts() {
      clearRecent();
      this.recentCache = { key: '', html: '' };
      var wrapper = this.results.querySelector('[data-ps-recent]');
      if (!wrapper) return;
      wrapper.classList.add('removing');
      wrapper.style.transition = 'opacity 0.25s ease';
      wrapper.style.opacity = '0';
      setTimeout(function () {
        wrapper.remove();
      }, reducedMotion() ? 0 : 260);
    }

    /* ---------- Miniaturas de color del mismo modelo ---------- */

    readSwatchCache(model) {
      try {
        var raw = sessionStorage.getItem(SWATCH_CACHE_PREFIX + model);
        if (!raw) return null;
        var data = JSON.parse(raw);
        return Date.now() - data.t > SWATCH_CACHE_TTL ? null : data.items;
      } catch (e) {
        return null;
      }
    }

    writeSwatchCache(model, items) {
      try {
        sessionStorage.setItem(SWATCH_CACHE_PREFIX + model, JSON.stringify({ t: Date.now(), items: items }));
      } catch (e) {
        /* sin almacenamiento */
      }
    }

    fetchModel(model) {
      var self = this;
      if (this.swatchRequests[model]) return this.swatchRequests[model];

      var cached = this.readSwatchCache(model);
      if (cached) {
        this.swatchRequests[model] = Promise.resolve(cached);
        return this.swatchRequests[model];
      }

      var url = new URL(this.urls.search, window.location.origin);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'none');
      url.searchParams.set('view', 'bk-model');
      url.searchParams.set('q', 'tag:"' + model.replace(/"/g, '') + '"');

      this.swatchRequests[model] = fetchText(url.toString())
        .then(function (text) {
          var items = JSON.parse(text).filter(function (item) {
            return item.modelo === model;
          });
          self.writeSwatchCache(model, items);
          return items;
        })
        .catch(function (error) {
          delete self.swatchRequests[model];
          console.warn('[BIKILA modelos]', model, error);
          return [];
        });
      return this.swatchRequests[model];
    }

    initSwatches(scope) {
      var self = this;
      scope.querySelectorAll('.ps-swatches:not([data-ready])').forEach(function (root) {
        root.dataset.ready = 'true';
        if (!root.dataset.bkModel) return;
        self.fetchModel(root.dataset.bkModel).then(function (items) {
          if (document.contains(root)) self.renderSwatches(root, items);
        });
      });
    }

    renderSwatches(root, items) {
      var current = root.dataset.bkCurrent;
      var gender = root.dataset.bkGender || '';
      var vendor = root.dataset.bkVendor || '';
      var max = Math.max(1, parseInt(root.dataset.bkMax, 10) || 4);

      var list = items
        .filter(function (item) {
          if (String(item.id) === current) return true;
          if (gender && item.genero !== gender) return false;
          if (vendor && item.vendor !== vendor) return false;
          if (!item.available) return false;
          return Boolean(item.thumb);
        })
        .sort(function (a, b) {
          if (String(a.id) === current) return -1;
          if (String(b.id) === current) return 1;
          return 0;
        });

      root.dataset.done = 'true';
      if (list.length < 2) {
        root.hidden = true;
        return;
      }

      this.swatchItems.set(root, list);
      var ul = root.querySelector('.ps-swatches__list');
      ul.textContent = '';

      var self = this;
      list.forEach(function (item, index) {
        var li = document.createElement('li');
        li.className = 'ps-swatches__item';
        if (index >= max) li.hidden = true;

        var colour = item.color ? item.color.toLowerCase() : '';
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'ps-swatches__thumb';
        button.dataset.id = String(item.id);
        button.title = colour || item.title;
        button.setAttribute('aria-pressed', String(String(item.id) === current));
        button.setAttribute(
          'aria-label',
          colour
            ? self.labels.swatchView.replace('__COLOR__', colour)
            : self.labels.swatchViewGeneric.replace('__TITLE__', item.title)
        );

        var img = document.createElement('img');
        img.src = item.thumb;
        if (item.thumb2x) img.srcset = item.thumb + ' 1x, ' + item.thumb2x + ' 2x';
        img.alt = '';
        img.loading = 'lazy';
        img.width = 32;
        img.height = 24;
        button.appendChild(img);
        li.appendChild(button);
        ul.appendChild(li);
      });

      var rest = list.length - max;
      if (rest > 0) {
        var moreItem = document.createElement('li');
        moreItem.className = 'ps-swatches__item';
        var more = document.createElement('button');
        more.type = 'button';
        more.className = 'ps-swatches__more';
        more.dataset.more = 'true';
        more.textContent = '+' + rest;
        more.setAttribute('aria-label', this.labels.swatchMore.replace('__COUNT__', String(rest)));
        moreItem.appendChild(more);
        ul.appendChild(moreItem);
      }
      root.hidden = false;
    }

    onSwatchClick(button) {
      var root = button.closest('.ps-swatches');

      if (button.dataset.more) {
        var hidden = Array.prototype.slice.call(root.querySelectorAll('.ps-swatches__item[hidden]'));
        hidden.forEach(function (li) {
          li.hidden = false;
        });
        button.closest('li').remove();
        return;
      }

      var list = this.swatchItems.get(root) || [];
      var item = list.find(function (entry) {
        return String(entry.id) === button.dataset.id;
      });
      if (item) this.showSwatch(root, item);
    }

    showSwatch(root, item) {
      var card = root.closest('.ps-card');
      if (!card) return;

      var img = card.querySelector('.ps-card__img');
      var links = Array.prototype.slice.call(card.querySelectorAll('a[data-ps-link]'));
      var price = card.querySelector('[data-ps-price]');

      if (!card.__original) {
        card.__original = {
          src: img ? img.getAttribute('src') : null,
          srcset: img ? img.getAttribute('srcset') : null,
          price: price ? price.outerHTML : null,
          hrefs: links.map(function (a) {
            return a.getAttribute('href');
          })
        };
      }

      var original = card.__original;
      var isOriginal = String(item.id) === root.dataset.bkCurrent;

      if (img) {
        if (isOriginal) {
          if (original.src) img.setAttribute('src', original.src);
          if (original.srcset) img.setAttribute('srcset', original.srcset);
          else img.removeAttribute('srcset');
        } else if (item.image) {
          img.removeAttribute('srcset');
          img.setAttribute('src', item.image);
          if (item.srcset) img.setAttribute('srcset', item.srcset);
          img.alt = '';
        }
      }
      card.classList.toggle('is-swapped', !isOriginal);

      links.forEach(function (a, i) {
        a.setAttribute('href', isOriginal ? original.hrefs[i] : item.url);
      });

      if (price) {
        var html = isOriginal ? original.price : item.priceHtml;
        if (html) price.outerHTML = html;
      }

      root.querySelectorAll('.ps-swatches__thumb').forEach(function (thumb) {
        thumb.setAttribute('aria-pressed', String(thumb.dataset.id === String(item.id)));
      });
    }
  }

  /* ---------- Arranque ---------- */

  function start() {
    var root = document.getElementById('ps-modal');
    if (!root || !root.hasAttribute('data-predictive-url')) return;

    var search = new PredictiveSearch(root);
    window.bikilaPredictiveSearch = search;

    // Cualquier enlace/botón de búsqueda del tema abre este panel.
    document.addEventListener(
      'click',
      function (event) {
        if (event.defaultPrevented) return;
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        var target = event.target instanceof Element ? event.target : null;
        var trigger = target && target.closest('[data-ps-open], [data-modal-toggle="#search-modal"]');
        if (!trigger) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        search.open(null, trigger);
      },
      true
    );

    // Atajo de teclado: Cmd/Ctrl + K
    document.addEventListener('keydown', function (event) {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && String(event.key || '').toLowerCase() === 'k') {
        event.preventDefault();
        search.toggle();
      }
    });

    // Precarga el estado inicial cuando el cliente muestra intención de buscar.
    var preload = function () {
      search.loadEmptyBase();
      search.loadRecentProducts();
    };
    ['pointerover', 'focusin'].forEach(function (name) {
      document.addEventListener(
        name,
        function (event) {
          var target = event.target instanceof Element ? event.target : null;
          if (target && target.closest('[data-ps-open], [data-ps-bar], [data-modal-toggle="#search-modal"]')) {
            preload();
          }
        },
        { passive: true }
      );
    });

    // Barra compacta del header: sustituye al campo de búsqueda en línea original del tema.
    var barTemplate = document.getElementById('ps-bar-template');
    if (barTemplate) {
      var oldSearches = document.querySelectorAll('.header__search');
      oldSearches.forEach(function (oldSearch) {
        var previous = oldSearch.previousElementSibling;
        if (previous && previous.hasAttribute('data-ps-bar')) return;
        oldSearch.parentNode.insertBefore(barTemplate.content.cloneNode(true), oldSearch);
      });
      if (oldSearches.length) document.documentElement.classList.add('ps-ready');
    }

    document.querySelectorAll('[data-ps-bar]').forEach(function (bar) {
      var input = bar.querySelector('input');
      var button = bar.querySelector('button');
      if (!input || !button) return;

      var openWithText = function () {
        var value = input.value;
        input.value = '';
        search.open(value.trim() ? value : null, input);
        // El cursor queda al final del texto del panel.
        try {
          var end = search.input.value.length;
          search.input.setSelectionRange(end, end);
        } catch (e) {
          /* el campo sigue siendo utilizable */
        }
      };

      input.addEventListener('input', function (event) {
        if (!event.isComposing && input.value.length) openWithText();
      });
      input.addEventListener('compositionend', function () {
        if (input.value.length) openWithText();
      });
      input.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' && !event.isComposing) {
          event.preventDefault();
          openWithText();
        }
      });
      button.addEventListener('click', openWithText);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
